import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, BookAudio, Users, ShieldCheck, Server, ArrowLeft, Layers, Gift, Loader2 } from "lucide-react";
import { LogoMark } from "../../components/Logo";
import { useLang } from "../../context/LanguageContext";
import { useAuth } from "../../context/AuthContext";

export default function AdminLayout() {
  const { t } = useLang();
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  // Resolving the token is a round trip, and during it `user` is null. Deciding
  // before it lands showed "Admin access required" to an admin on every reload.
  if (loading) {
    return (
      <div className="py-32 flex justify-center" role="status" aria-label="Loading">
        <Loader2 className="w-6 h-6 text-muted-foreground/60 animate-spin" />
      </div>
    );
  }

  if (!user || !isAdmin) {
    return (
      <div className="py-32 text-center">
        <ShieldCheck className="w-12 h-12 text-muted-foreground/50 mx-auto mb-4" />
        <p className="text-muted-foreground">Admin access required.</p>
        <button onClick={() => navigate("/login")} className="mt-4 text-brand-400 font-medium">
          {t("signin")}
        </button>
      </div>
    );
  }

  const links = [
    { to: "/admin", end: true, icon: LayoutDashboard, label: t("admin_dashboard") },
    { to: "/admin/books", icon: BookAudio, label: t("admin_books") },
    { to: "/admin/collections", icon: Layers, label: "Collections" },
    { to: "/admin/users", icon: Users, label: t("admin_users") },
    { to: "/admin/redemptions", icon: Gift, label: "Codes" },
    { to: "/admin/bunny", icon: Server, label: t("admin_bunny") },
  ];

  return (
    <div className="max-w-[1500px] mx-auto px-4 sm:px-6 lg:px-8 py-6 flex gap-6">
      <aside className="hidden md:block w-60 shrink-0">
        <div className="sticky top-24">
          <div className="flex items-center gap-2 mb-6 px-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-300 to-brand-600 flex items-center justify-center">
              <LogoMark className="w-4 h-4 text-brand-950" strokeWidth={2.2} />
            </div>
            <div>
              <p className="font-display font-semibold leading-none">Trellure</p>
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">CMS</p>
            </div>
          </div>
          <nav className="space-y-1">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-brand-500/15 text-brand-400 border border-brand-500/25"
                      : "text-muted-foreground hover:text-foreground hover:bg-secondary border border-transparent"
                  }`
                }
              >
                <l.icon className="w-4 h-4" /> {l.label}
              </NavLink>
            ))}
          </nav>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 mt-6 px-3 text-sm text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> {t("nav_home")}
          </button>
        </div>
      </aside>

      {/* mobile nav */}
      {/* Above the player (z-50), which is also fixed to the bottom and was
          covering this whole bar whenever something was playing. */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-[60] bg-card border-t border-border flex justify-around py-2">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.end}
            className={({ isActive }) =>
              `flex flex-col items-center gap-0.5 px-3 py-1 text-[10px] ${isActive ? "text-brand-400" : "text-muted-foreground"}`
            }
          >
            <l.icon className="w-5 h-5" />
            {l.label.split(" ")[0]}
          </NavLink>
        ))}
      </div>

      <main className="flex-1 min-w-0 pb-20 md:pb-0">
        <Outlet />
      </main>
    </div>
  );
}
